import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Form, Input, Select, Button, message, Spin } from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {CustomerProfileDTO, FspDto, FspType} from "@/shared/api/model";
import {useGetFspsByType} from "@/hooks/admin/profile";
import {FormInstance} from "antd/es/form";

const { Option } = Select;

interface PaymentInformationSectionProps {
  initialValues?: CustomerProfileDTO;
  onPaymentInfoChange?: (values: Partial<CustomerProfileDTO>) => void;
  form: FormInstance;
}

export const PaymentInformationSection = forwardRef<any, PaymentInformationSectionProps>(({ 
  initialValues, 
  onPaymentInfoChange,
  form
}, ref) => {
  const { dark } = useTheme();
  const [selectedFspType, setSelectedFspType] = useState<FspType | null>(null);
  const [fspQuery] = useGetFspsByType(selectedFspType || FspType.BANK);
  const { data: fsps, isLoading: isFspsLoading } = fspQuery;

  // Expose form methods to parent component via ref
  useImperativeHandle(ref, () => ({
    form,
    validate: () => form.validateFields(),
    getFieldsValue: () => form.getFieldsValue(),
  }));

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue({
        disbursmentFspType: initialValues.disbursmentFspType,
        disbursementFspCode: initialValues.disbursementFspCode,
        disbursementAccRef: initialValues.disbursementAccRef,
        accountName: initialValues.accountName,
      });
      setSelectedFspType(initialValues.disbursmentFspType as FspType);
    }
  }, [initialValues, form]);

  // Update parent component when form values change
  const handleValuesChange = () => {
    if (onPaymentInfoChange) {
      const values = form.getFieldsValue();
      onPaymentInfoChange(values);
    }
  };

  // Handle FSP type change
  const handleFspTypeChange = (value: FspType) => {
    setSelectedFspType(value);
    // Reset related fields when type changes
    form.setFieldsValue({
      disbursementFspCode: undefined,
      disbursementAccRef: undefined,
      accountName: value === FspType.BANK ? initialValues?.accountName : undefined
    });
    handleValuesChange();
  };

  // Filter option function for Select components
  const filterOption = (input: string, option: any) => 
    option?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0;

  return (
    <div className={dark ? 'dark-mode' : ''}>
      <Form.Item name="disbursmentFspType" label="Disbursement Channel" rules={[{ required: true }]}>
        <Select onChange={(value) => handleFspTypeChange(value as FspType)}>
          <Option value={FspType.BANK}>Bank</Option>
          <Option value={FspType.MNO}>Mobile Money</Option>
        </Select>
      </Form.Item>
      
      {selectedFspType === FspType.BANK ? (
        <>
          <Form.Item name="disbursementFspCode" label="Bank" rules={[{ required: true }]}>
            <Select 
              loading={isFspsLoading} 
              onChange={handleValuesChange}
              showSearch
              placeholder="Search for a bank"
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {fsps?.map((fsp: FspDto) => (
                <Option key={fsp.code} value={fsp.code}>{fsp.institutionName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="disbursementAccRef" label="Account Number" rules={[{ required: true }]}>
            <Input onChange={handleValuesChange} />
          </Form.Item>
          <Form.Item name="accountName" label="Account Name" rules={[{ required: true }]}>
            <Input onChange={handleValuesChange} />
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item name="disbursementFspCode" label="Mobile Money Provider" rules={[{ required: true }]}>
            <Select 
              loading={isFspsLoading} 
              onChange={handleValuesChange}
              showSearch
              placeholder="Search for a mobile money provider"
              optionFilterProp="children"
              filterOption={filterOption}
            >
              {fsps?.map((fsp: FspDto) => (
                <Option key={fsp.code} value={fsp.code}>{fsp.institutionName}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="disbursementAccRef" label="Mobile Number" rules={[{ required: true }]}>
            <Input onChange={handleValuesChange} />
          </Form.Item>
        </>
      )}
    </div>
  );
});